


















import { defineComponent, reactive, toRefs, PropType, computed, watch, onMounted } from '@vue/composition-api';
import { SheetField, AnswerText, Point, KaitouModeEnum, StudentSheetStatusEnum } from "../modules/digital_paper/AnswerInput"
import * as Common from "../commons/common"
// @ts-ignore
import VueDragResize from "vue-drag-resize";
// @ts-ignore
import { longClickDirective } from 'vue-long-click'
const longClickInstance = longClickDirective({delay: 600, interval: 0})


export interface Data {
  isDrawing: boolean
  startPos: Point
  endPos: Point
  scrollPos: Point
  isTextTouching: boolean
  isDragging: boolean
  isScrolling: boolean
  dragMinRange: number
  kaitouModeCache: number
  KaitouModeEnum: Readonly<{None: number; Text: number; FreeLine: number; StraightLine: number; Eraser: number; Delete: number;}>
}

export default defineComponent({
  directives: {
    longclick: longClickInstance
  },
  props: {
    sheetField: {
      type: SheetField as PropType<SheetField | null>,
      required: true
    },
    selectedSheetField: {
      type: SheetField || null as PropType<SheetField | null>,
      required: false,
      default: null
    },
    selectedAnswerText: {
      type: AnswerText || null as PropType<AnswerText | null>,
      required: false,
      default: null,
    },
    scale: {
      type: Number as PropType<number>,
      required: true
    },
    kaitouMode: {
      type: Number as PropType<number>,
      required: true
    },
    fontSize: {
      type: Number as PropType<number>,
      required: true
    },
    lineHeight: {
      type: Number as PropType<number>,
      required: true
    },
    direction: {
      type: Number as PropType<number>,
      required: true
    },
    keyCode: {
      type: String as PropType<string>,
      required: false,
      default: null
    },
    touchLength: {
      type: Number as PropType<number>,
      required: true
    },
    cutMargin: {
      type: Number as PropType<number>,
      required: true
    },
    status: {
      type: Number as PropType<number>,
      required: true
    },
    drawColor: {
      type: String as PropType<string>,
      required: false,
      default: null
    },
    thickness: {
      type: Number as PropType<number>,
      required: true,
    },
  },
  components: {
    "vue-drag-resize": VueDragResize,
  },
  setup (props, context) {

    /************************************************************
     * Data
     ************************************************************/

    const state = reactive<Data>({
      isDrawing: false,
      startPos: new Point(0, 0),
      endPos: new Point(0, 0),
      scrollPos: new Point(0, 0),
      isTextTouching: false,
      isDragging: false,
      isScrolling: false,
      dragMinRange: 5,
      kaitouModeCache: null,
      KaitouModeEnum: KaitouModeEnum,
    });

    /****************************************
     * 倍率を加味した画像の横幅
     ****************************************/

    const parentW = computed(() => {
      return (props.sheetField.width + (props.cutMargin * 2)) * props.scale;
    });

    /****************************************
     * 倍率を加味した画像の横幅
     ****************************************/

    const parentH = computed(() => {
      return (props.sheetField.height + (props.cutMargin * 2)) * props.scale;
    });

    /************************************************************
     * 画像の親DivのCSSスタイル
     ************************************************************/

    const imageWrapStyle = computed((): any => {
      const style: any = {};
      style["position"] = "absolute";
      style["width"] = `${parentW.value}px`;
      style["height"] = `${parentH.value}px`;
      style["left"] = `${(props.sheetField.x - props.cutMargin) * props.scale}px`;
      style["top"] = `${(props.sheetField.y - props.cutMargin) * props.scale}px`;

      if(props.status == StudentSheetStatusEnum.Answering && isSelectedField.value){
        style["box-shadow"] = "0 0 4px 4px blue";
        style["z-index"] = "3";
      }else{
        style["box-shadow"] = "";
        style["z-index"] = "1";
      }
      return style;
    });

    /************************************************************
     * 自身が解答対象として選択中の設問か
     ************************************************************/

    const isSelectedField = computed((): any => {
      return props.sheetField == props.selectedSheetField;
    });

    /************************************************************
     * canvasのCSSスタイル
     ************************************************************/

    const canvasStyle = computed((): any => {
      const style: any = {};
      style["width"] = `${parentW.value}px`;
      style["height"] = `${parentH.value}px`;
      style["z-index"] = isTensakuMode.value ? 2 : 1;
      return style;
    });

    /************************************************************
     * 解答テキストのCSSスタイル
     ************************************************************/

    const textStyle = computed((): any => {
      const style: any = {};
      style["z-index"] = isTensakuMode.value ? 1 : 2;
      return style;
    });

    /************************************************************
     * 現在のモードが添削系か判定
     ************************************************************/

    const isTensakuMode = computed(() => {
      return props.kaitouMode != state.KaitouModeEnum.None && props.kaitouMode != state.KaitouModeEnum.Text && props.kaitouMode != state.KaitouModeEnum.Delete;
    });

    /************************************************************
     * AnswerTextスタンプ mousedown
     ************************************************************/

    const textMouseDown = () => {
      state.isTextTouching = true;
    }

    /************************************************************
     * AnswerTextスタンプ mouseup
     ************************************************************/

    const textMouseUp = (answerText: AnswerText) => {
      state.isTextTouching = false;
      if(!props.sheetField.answer_texts.includes(answerText)){
        return;
      }

      if((props.kaitouMode == KaitouModeEnum.None || props.kaitouMode == KaitouModeEnum.Text) && props.selectedAnswerText != answerText){
        context.emit("change-selected-answer-text", answerText);
      }else if(props.kaitouMode == KaitouModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-answer-text", answerText);
      }
    }

    /************************************************************
     * マルチタップ中か判定
     * 2本以上の指が検出されている、または1本はテキスト、それ以外に指が検出されている場合にtrue
     * テキストタッチはtouchLengthに加算されない場合があるのでisTextTouchingで判定する
     ************************************************************/

    const isMultiTouching = computed((): any => {
      return props.touchLength >=2 || (props.touchLength == 1 && state.isTextTouching);
    });

    /************************************************************
     * マウス座標取得
     ************************************************************/

    const getMousePos = (e: MouseEvent) => {
      const x: number = e.offsetX / props.scale;
      const y: number = e.offsetY / props.scale;
      return new Point(x, y);
    };

    /************************************************************
     * canvas MouseDown
     ************************************************************/

    const canvasMouseDown = (e: MouseEvent) => {
      
      const canvas = e.target as HTMLCanvasElement;
      state.startPos = getMousePos(e);

      if((!isTensakuMode.value || props.keyCode == "t") && props.keyCode != "e" && props.keyCode != "d" && props.keyCode != "m"){
        if(state.isDragging){
          state.isDragging = false;
        }else if(props.kaitouMode == KaitouModeEnum.Text || props.keyCode == "t"){
          const answerText = createAnswerText(state.startPos);
          context.emit("add-answer-text", answerText);
          context.emit("change-kaitou-mode-from-child", KaitouModeEnum.None);
        }else{
          context.emit("change-selected-answer-text", null);
        }
      }else{

        // 線描画モード
        if(props.keyCode == "d"){
          state.kaitouModeCache = props.kaitouMode;
          context.emit("change-kaitou-mode-from-child", KaitouModeEnum.FreeLine);
        }else if(props.keyCode == "e"){
          state.kaitouModeCache = props.kaitouMode;
          context.emit("change-kaitou-mode-from-child", KaitouModeEnum.Eraser);
        }

        e.preventDefault();
        state.isDrawing = true;
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = getStrokeStyle();
      }
    };

    /************************************************************
     * strokeStyle設定
     ************************************************************/

    const getStrokeStyle = () => {
      const r = parseInt(props.drawColor.slice(1, 3), 16);
      const g = parseInt(props.drawColor.slice(3, 5), 16);
      const b = parseInt(props.drawColor.slice(5, 7), 16);

      let alpha = 0.8;
      if(props.kaitouMode == KaitouModeEnum.Eraser){
        alpha = 1.0;
      }
debugger
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    /************************************************************
     * canvas MouseMove
     ************************************************************/

    const canvasMouseMove = (e: MouseEvent) => {
      
      if(!state.isDrawing || (props.kaitouMode != state.KaitouModeEnum.FreeLine && props.kaitouMode != state.KaitouModeEnum.Eraser)){
        return;
      }

      state.endPos = getMousePos(e);
      const ctx = (e.target as HTMLCanvasElement).getContext("2d");
      drawLine(ctx);
      state.startPos = state.endPos;
    };


    /************************************************************
     * canvas MouseUp
     ************************************************************/

    const canvasMouseUp = (e: MouseEvent) => {
      
      if(!state.isDrawing){
        return;
      }

      const canvas = e.target as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      
      if(props.kaitouMode == state.KaitouModeEnum.StraightLine){
        state.endPos = getMousePos(e);
        const ctx = canvas.getContext("2d");
        drawLine(ctx);
      }
      drawEnd(canvas, ctx);

      if(state.kaitouModeCache != null){
        context.emit("change-kaitou-mode-from-child", state.kaitouModeCache);
        state.kaitouModeCache = null;
      }
    };

    /************************************************************
     * canvas MouseLeave
     ************************************************************/

    const canvasMouseLeave = (e: MouseEvent) => {
      if(state.isDrawing){
        const canvas = e.target as HTMLCanvasElement;
        const ctx = canvas.getContext("2d");
        drawEnd(canvas, ctx);

        if(state.kaitouModeCache != null){
          context.emit("change-kaitou-mode-from-child", state.kaitouModeCache);
          state.kaitouModeCache = null;
        }
      }
    };

    /************************************************************
     * canvas Touchイベントの座標取得
     ************************************************************/

    const getTouchPos = (t: Touch, canvas: HTMLCanvasElement) => {
      const x: number = (t.clientX - canvas.getBoundingClientRect().left) / props.scale;
      const y: number = (t.clientY - canvas.getBoundingClientRect().top) / props.scale;
      return new Point(x, y);
    }

    /************************************************************
     * canvas TouchStart
     ************************************************************/

    const canvasTouchStart = (e: TouchEvent) => {

      const canvas = (e as TouchEvent).target as HTMLCanvasElement;
      state.startPos = getTouchPos(e.changedTouches[0], canvas);
      state.scrollPos = new Point(canvas.scrollLeft, canvas.scrollTop);

      // 添削モード以外はリターン
      if(isTensakuMode.value){
        e.preventDefault();
        state.isDrawing = true;
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = getStrokeStyle();
      }else if(state.isDragging){
        state.isDragging = false;
      }else if(props.kaitouMode == KaitouModeEnum.Text && !isMultiTouching.value){
        const answerText = createAnswerText(state.startPos);
        context.emit("add-answer-text", answerText);
        context.emit("change-kaitou-mode-from-child", KaitouModeEnum.None);
      }else{
        context.emit("change-selected-answer-text", null);
      }
    };

    /************************************************************
     * canvas TouchMove
     ************************************************************/

    const canvasTouchMove = (e: TouchEvent) => {
      
      if(!state.isDrawing){
        const canvas: HTMLCanvasElement = document.getElementById(`canvas-${props.direction}-${props.sheetField.id}`) as HTMLCanvasElement;
        const endPos = getTouchPos((e as TouchEvent).changedTouches[0], canvas);
        const scrollEndPos = new Point(canvas.scrollLeft, canvas.scrollTop);
        const limit = 5;
        if(endPos.x - state.startPos.x > limit || endPos.x - state.startPos.x < -limit || endPos.y - state.startPos.y > limit || endPos.y - state.startPos.y < -limit
          || scrollEndPos.x - state.scrollPos.x >  limit || scrollEndPos.x - state.scrollPos.x < -limit || scrollEndPos.y - state.scrollPos.y >  limit || scrollEndPos.y - state.scrollPos.y < -limit){
          state.isScrolling = true;
        }
      }else{
        e.preventDefault();

        if(props.kaitouMode == state.KaitouModeEnum.FreeLine || props.kaitouMode == state.KaitouModeEnum.Eraser){
          const canvas = e.target as HTMLCanvasElement;
          state.endPos = getTouchPos(e.changedTouches[0], canvas);
          const ctx = canvas.getContext("2d");
          drawLine(ctx);
          state.startPos = state.endPos;
        }
      }
    };

    /************************************************************
     * canvas TouchEnd
     ************************************************************/

    const canvasTouchEnd = (e: TouchEvent) => {

      state.isScrolling = false;
      
      if(!state.isDrawing){
        return;
      }

      e.preventDefault();

      const canvas = e.target as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");

      if(props.kaitouMode == state.KaitouModeEnum.StraightLine){
        state.endPos = getTouchPos(e.changedTouches[0], canvas);
        const ctx = canvas.getContext("2d");
        drawLine(ctx);
      }

      drawEnd(canvas, ctx);
    };

    /************************************************************
     * 線の種別による太さを取得
     ************************************************************/

    const getThickness = () => {
      return props.kaitouMode == KaitouModeEnum.Eraser ? props.thickness * 8 : props.thickness;
    }

    /************************************************************
     * canvas 自由線・消しゴム描画
     ************************************************************/

    const drawLine = (ctx: CanvasRenderingContext2D) => {
      if(props.kaitouMode == KaitouModeEnum.Eraser){
        ctx.globalCompositeOperation = "destination-out";
        ctx.lineWidth = getThickness();
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.endPos.x, state.endPos.y);
        ctx.stroke();
        // ctx.beginPath();
        // ctx.fillRect(state.startPos.x - (2 * props.scale), state.startPos.y - (2 * props.scale), thickness.value, thickness.value);
      }else {
        // drawLineSmooth(ctx);
        ctx.globalCompositeOperation = "source-over";
        ctx.lineWidth = getThickness();
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.endPos.x, state.endPos.y);
        ctx.stroke();
      }
    };

    /************************************************************
     * canvas 編集完了後処理
     ************************************************************/

    const drawEnd = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
      state.isDrawing = false;
      const base64Canvas = canvas.toDataURL("image/png").replace("data:image/png;base64,", "");
      props.sheetField.base64_canvas = base64Canvas;
      props.sheetField.is_canvas_updated = true;
      const canvasImage = new Image();
      canvasImage.src = `data:image/png;base64,${props.sheetField.base64_canvas}`;
      canvasImage.onload = () => {
        setIsCanvasDrawed(canvasImage);
      };
    }

    /************************************************************
     * AnswerTextインスタンス生成
     ************************************************************/

    const createAnswerText = (point: Point) => {
      const size = Common.getTextSize("", props.fontSize, props.lineHeight);
      const answerText = new AnswerText();
      answerText.sheet_field_id = props.sheetField.id;
      answerText.idx = props.sheetField.answer_text_idx++;
      answerText.font_size = props.fontSize;
      answerText.line_height = 0;
      answerText.color = "#000000";
      answerText.x = point.x;
      answerText.y = point.y;
      answerText.width = size.width;
      answerText.height = size.height;
      answerText.base64_text = Common.createBase64Text(answerText);
      return answerText;
    }

    /************************************************************
     * テキスト画像ドラッグ中
     ************************************************************/

    const textDragging = (answerText: AnswerText, rect: any) => {
      // 移動距離が5px未満はドラッグ中とは判定しない
      const movedX = rect.left - answerText.x * props.scale;
      const movedY = rect.top - answerText.y * props.scale;
      state.isDragging = movedX > state.dragMinRange || movedX < -state.dragMinRange || movedY > state.dragMinRange || movedY < -state.dragMinRange;
    }

    /************************************************************
     * テキスト画像ドラッグ終了
     ************************************************************/

    const textDragStop = (answerText: AnswerText, rect: any) => {

      if(rect.left < 0){
        answerText.x = 0;
      }else if(rect.left + rect.width > parentW.value){
        answerText.x = (parentW.value - rect.width) / props.scale;
      }else{
        answerText.x = rect.left / props.scale;
      }

      if(rect.top < 0){
        answerText.y = 0;
      }else if(rect.top + rect.height > parentH.value){
        answerText.y = (parentH.value - rect.height) / props.scale;
      }else{
        answerText.y = rect.top / props.scale;
      }
      answerText.x = rect.left / props.scale;
      answerText.y = rect.top / props.scale;
    }

    /************************************************************
     * canvas描画
     * 縦・横表示切り替え時に親から呼ばれる
     ************************************************************/

    const renderCanvas = () => {
      const canvas = document.getElementById(`canvas-${props.direction}-${props.sheetField.id}`) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      if(props.sheetField.base64_canvas){
        const canvasImage = new Image();
        canvasImage.src = `data:image/png;base64,${props.sheetField.base64_canvas}`;
        canvasImage.onload = () => {
          ctx.drawImage(canvasImage, 0, 0);
          setIsCanvasDrawed(canvasImage);
        };
      }else{
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }

    /************************************************************
     * テキスト長押し削除
     ************************************************************/

    const textLongClicked = (answerText: AnswerText) => {
      if(context.root.$store.state.isMobile && state.isTextTouching && !state.isDragging && props.kaitouMode != KaitouModeEnum.Delete && !isMultiTouching.value){
        // 得点スタンプ削除emit
        context.emit("delete-answer-text", answerText);
      }
    }

    /************************************************************
     * 画像上を長押しテキスト出力
     ************************************************************/

    const canvasLongClicked = () => {
      if(context.root.$store.state.isMobile && props.kaitouMode == KaitouModeEnum.None && !state.isScrolling && !isMultiTouching.value){
        const answerText = createAnswerText(state.startPos);
        context.emit("add-answer-text", answerText);
        context.emit("change-kaitou-mode-from-child", KaitouModeEnum.None);
      }
    }

    /************************************************************
     * mounted
     ************************************************************/

    onMounted(async () => {
      const canvas = document.getElementById(`canvas-${props.direction}-${props.sheetField.id}`) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      ctx.lineJoin = "round";
      ctx.lineCap = "round";

      props.sheetField.answer_texts.forEach(x => {
        x.base64_text = Common.createBase64Text(x);
      });

      if(props.sheetField.base64_canvas){
        const canvasImage = new Image();
        canvasImage.src = `data:image/png;base64,${props.sheetField.base64_canvas}`;
        canvasImage.onload = () => {
          ctx.drawImage(canvasImage, 0, 0);
          setIsCanvasDrawed(canvasImage);
        };
      }
    })

    /************************************************************
     * is_canvas_drawed設定
     ************************************************************/

    const setIsCanvasDrawed = (canvasImage: HTMLImageElement) => {
      const canvas = document.createElement("canvas") as HTMLCanvasElement;
      const canvasW = props.sheetField.width + props.cutMargin * 2;
      const canvasH = props.sheetField.height + props.cutMargin * 2
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(canvasImage, 0, 0);
      const bitCnt = ctx.getImageData(0, 0, canvasW, canvasH).data.filter(x => x > 0).length;
      props.sheetField.is_canvas_drawed = bitCnt > 60;
    }

    return {
      ...toRefs(state),
      imageWrapStyle,
      canvasStyle,
      textStyle,
      canvasMouseDown,
      canvasMouseMove,
      canvasMouseUp,
      canvasMouseLeave,
      canvasTouchStart,
      canvasTouchMove,
      canvasTouchEnd,
      canvasLongClicked,
      renderCanvas,
      textMouseDown,
      textMouseUp,
      textDragging,
      textDragStop,
      isSelectedField,
      textLongClicked,
      isMultiTouching,
    }
  }
});
